import { useState } from 'react';
import type { OnboardingInstance, OnboardingStep } from '@hospasense/contracts';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import {
  useAddEvidence,
  useApprovalMutation,
  useOnboardingTemplates,
  useOrgOnboarding,
  useStartOnboarding,
  useUpdateStep,
} from '../../lib/queries-s2.js';
import { formatDate } from '../../lib/format.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  Select,
  StatusPill,
  Textarea,
  useToast,
} from '../../components/ui/index.js';

const STEP_TONES = {
  pending: 'neutral',
  in_progress: 'info',
  blocked: 'error',
  complete: 'success',
  waived: 'warning',
} as const;

/** Onboarding checklist tab of the Organisation Workspace. */
export function ChecklistTab({ orgId }: { orgId: string }) {
  const { hasPermission } = useAuth();
  const canRead = hasPermission('onboarding.read');
  const canManage = hasPermission('onboarding.manage');
  const query = useOrgOnboarding(orgId, canRead);
  const templates = useOnboardingTemplates(canManage);
  const start = useStartOnboarding(orgId);
  const approvals = useApprovalMutation();
  const toast = useToast();
  const [templateId, setTemplateId] = useState('');

  if (!canRead)
    return <EmptyState title="No access" description="Your role cannot view onboarding." />;
  if (query.isLoading) return <LoadingState />;

  if (query.isError || !query.data) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    if (!notFound) {
      return (
        <ErrorState
          description="Onboarding could not be loaded."
          onRetry={() => void query.refetch()}
        />
      );
    }
    return (
      <Card>
        <EmptyState
          title="Onboarding not started"
          description="Instantiate a checklist from a template to begin taking this organisation live."
          action={
            canManage ? (
              <div className="flex items-end gap-2">
                <Select
                  label="Template"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                >
                  <option value="">Choose…</option>
                  {(templates.data?.items ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.stepCount} steps)
                    </option>
                  ))}
                </Select>
                <Button
                  disabled={!templateId}
                  loading={start.isPending}
                  onClick={() =>
                    void start
                      .mutateAsync({ templateId })
                      .then(() => toast.success('Onboarding started'))
                      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Failed'))
                  }
                >
                  Start onboarding
                </Button>
              </div>
            ) : undefined
          }
        />
      </Card>
    );
  }

  const instance = query.data;
  return (
    <div className="space-y-4">
      <ReadinessBanner
        instance={instance}
        onRequestGoLive={requestGoLive}
        busy={approvals.create.isPending}
      />
      <ol className="space-y-3">
        {instance.steps.map((step) => (
          <StepCard
            key={step.id}
            step={step}
            orgId={orgId}
            canManage={canManage && instance.status === 'in_progress'}
          />
        ))}
      </ol>
    </div>
  );

  function requestGoLive(): void {
    void approvals.create
      .mutateAsync({
        actionType: 'onboarding.go_live',
        reason: 'All onboarding steps satisfied; requesting go-live (two-person approval).',
        payload: { organisationId: orgId, instanceId: instance!.id },
        submit: true,
      })
      .then(() => toast.success('Go-live approval requested — see the Approval Inbox'))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Request failed'));
  }
}

function ReadinessBanner({
  instance,
  onRequestGoLive,
  busy,
}: {
  instance: OnboardingInstance;
  onRequestGoLive: () => void;
  busy: boolean;
}) {
  const { hasPermission } = useAuth();
  const r = instance.readiness;
  if (instance.status === 'live') {
    return (
      <p className="rounded-lg bg-success-bg px-3 py-2 text-sm text-success" role="status">
        Live since {formatDate(instance.wentLiveAt)}.
      </p>
    );
  }
  if (r.ready) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-success-bg px-3 py-2">
        <p className="text-sm text-success">
          All {r.totalSteps} steps satisfied — ready for go-live (requires two-person approval).
        </p>
        {hasPermission('approval.request') && (
          <Button size="sm" onClick={onRequestGoLive} loading={busy}>
            Request go-live approval
          </Button>
        )}
      </div>
    );
  }
  return (
    <p className="rounded-lg bg-warning-bg px-3 py-2 text-sm text-warning" role="status">
      {r.completedSteps}/{r.totalSteps} steps satisfied · {r.blockers.length} blockers remain.
      Go-live stays locked until every step is complete or validly waived.
    </p>
  );
}

function StepCard({
  step,
  orgId,
  canManage,
}: {
  step: OnboardingStep;
  orgId: string;
  canManage: boolean;
}) {
  const { hasPermission } = useAuth();
  const update = useUpdateStep();
  const evidence = useAddEvidence();
  const approvals = useApprovalMutation();
  const toast = useToast();
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');

  const move = (status: 'pending' | 'in_progress' | 'complete', blockerReason?: string): void => {
    void update
      .mutateAsync({
        stepId: step.id,
        body: {
          status: blockerReason ? 'blocked' : status,
          blockerReason,
          expectedVersion: step.version,
        },
      })
      .then(() => toast.success('Step updated'))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Update failed'));
  };

  return (
    <li>
      <Card>
        <CardBody className="space-y-2 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-text-primary">
                {step.sortOrder}. {step.title}
                {step.evidenceRequired && (
                  <span className="ml-2">
                    <Badge tone="info">Evidence required</Badge>
                  </span>
                )}
              </p>
              {step.description && <p className="text-xs text-text-muted">{step.description}</p>}
              <p className="mt-0.5 text-xs text-text-muted">
                {step.ownerDisplayName ?? 'Unowned'} · due {formatDate(step.dueDate)}
                {step.dependsOnKeys.length > 0 && ` · after: ${step.dependsOnKeys.join(', ')}`}
              </p>
            </div>
            <StatusPill tone={STEP_TONES[step.status]} label={step.status.replace('_', ' ')} />
          </div>
          {step.status === 'blocked' && step.blockerReason && (
            <p className="rounded-lg bg-error-bg-subtle px-2 py-1 text-xs text-error">
              Blocked: {step.blockerReason}
            </p>
          )}
          {step.waiver && (
            <p className="rounded-lg bg-warning-bg-subtle px-2 py-1 text-xs text-warning">
              Waived by {step.waiver.approvedBy ?? '—'} until {formatDate(step.waiver.expiresAt)}
              {step.waiver.expired && ' (EXPIRED — no longer satisfies readiness)'} ·{' '}
              {step.waiver.reason}
            </p>
          )}
          {step.evidence.length > 0 && (
            <ul className="space-y-0.5">
              {step.evidence.map((e) => (
                <li key={e.id} className="text-xs text-text-secondary">
                  📎 {e.title}{' '}
                  <span className="text-text-muted">
                    ({e.kind}) · {e.createdBy ?? '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {canManage && (
            <div className="flex flex-wrap gap-2 pt-1">
              {step.status === 'pending' && (
                <Button size="sm" variant="outline" onClick={() => move('in_progress')}>
                  Start
                </Button>
              )}
              {step.status === 'in_progress' && (
                <Button size="sm" onClick={() => move('complete')}>
                  Mark complete
                </Button>
              )}
              {(step.status === 'pending' || step.status === 'in_progress') && (
                <Button size="sm" variant="outline" onClick={() => setBlockOpen(true)}>
                  Block
                </Button>
              )}
              {step.status === 'blocked' && (
                <Button size="sm" variant="outline" onClick={() => move('pending')}>
                  Unblock
                </Button>
              )}
              {step.status === 'complete' && (
                <Button size="sm" variant="ghost" onClick={() => move('in_progress')}>
                  Reopen
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setEvidenceOpen(true)}>
                Add evidence
              </Button>
              {step.status !== 'complete' &&
                step.status !== 'waived' &&
                hasPermission('approval.request') && (
                  <Button size="sm" variant="ghost" onClick={() => setWaiveOpen(true)}>
                    Request waiver
                  </Button>
                )}
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={evidenceOpen}
        onClose={() => setEvidenceOpen(false)}
        title={`Evidence for: ${step.title}`}
        footer={
          <Button
            loading={evidence.isPending}
            disabled={title.trim().length < 3 || text.trim().length < 3}
            onClick={() =>
              void evidence
                .mutateAsync({ stepId: step.id, kind: 'link', title, content: text })
                .then(() => {
                  toast.success('Evidence attached');
                  setEvidenceOpen(false);
                  setTitle('');
                  setText('');
                })
                .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Failed'))
            }
          >
            Attach
          </Button>
        }
      >
        <div className="space-y-3">
          <Input label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />
          <Input
            label="Link or reference"
            required
            hint="Evidence store link or document reference — never clinical content."
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      </Modal>

      <Modal
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        title={`Block: ${step.title}`}
        footer={
          <Button
            variant="danger"
            disabled={text.trim().length < 5}
            onClick={() => {
              move('pending', text);
              setBlockOpen(false);
              setText('');
            }}
          >
            Block step
          </Button>
        }
      >
        <Textarea
          label="Blocker reason"
          required
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Modal>

      <Modal
        open={waiveOpen}
        onClose={() => setWaiveOpen(false)}
        title={`Request waiver: ${step.title}`}
        footer={
          <Button
            loading={approvals.create.isPending}
            disabled={text.trim().length < 10}
            onClick={() =>
              void approvals.create
                .mutateAsync({
                  actionType: 'onboarding.step.waive',
                  reason: `Waiver requested for step "${step.title}"`,
                  payload: {
                    organisationId: orgId,
                    stepId: step.id,
                    waiverReason: text,
                    waiverExpiresAt: new Date(Date.now() + 30 * 86400_000).toISOString(),
                  },
                  submit: true,
                })
                .then(() => {
                  toast.success('Waiver approval requested (30-day expiry)');
                  setWaiveOpen(false);
                  setText('');
                })
                .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Failed'))
            }
          >
            Submit for approval
          </Button>
        }
      >
        <div className="space-y-2">
          <p className="text-xs text-text-muted">
            Waivers need approval by a Product Owner or the Security & Privacy Officer (someone
            other than you) and expire after 30 days.
          </p>
          <Textarea
            label="Waiver justification"
            required
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      </Modal>
    </li>
  );
}
